import {
	mapOwnership,
	mapRoadSeal,
	mapRoadSystem,
	lookupCell,
	lookupAttrCell
} from '#lib/common/mappings.js';
import type { AttributesLayoutHelper, CellContent } from '#lib/components/Attributes.types.js';
import type { FeatureRow, TableColumnDef } from '#lib/components/table/tanstackFeatures.js';
import { formatUnitValue, getUnitLabel, type Quantity, type UnitField } from '#lib/intlUnits.js';
import { unitSystemStore } from '#lib/stores/unitSystemStore.svelte.js';
import {
	mapPriorityDescription,
	priorityDescriptionOrder,
	mapNotPrioritizedReason
} from './prioritize';
import { priorityLabel, sortByOrder } from '../priority';
import { PRIORITIZE_ROAD_NAME_MIN } from './versionSupport';
import { pffaLabelFor } from './legendHelpers';

// --- Feature property types ---

export type TIdentify = {
	id: number;
	county: string;
	road_name: string;
	ownership: number;
	road_system: number;
	speed_limit: number;
	road_seal: number;
	curve_context: string;
	curve_radius: number;
	curve_direction: string;
	start_position: string;
	end_position: string;
	fhwa_curve_advisory_speed: number;
	accuracy_warning: string | undefined;
	aadt_band: string;
};

export type TPrioritize = {
	id: number;
	county: string;
	corridor_id: string;
	// Only published from the `PRIORITIZE_ROAD_NAME_MIN` version line.
	road_name?: string;
	ownership: number;
	road_system: number;
	speed_limit: number;
	road_seal: number;
	priority_rating: string;
	priority_rating_rank: number;
	accuracy_warning: string | undefined;
	aadt_band: string;
	alignment_category: string;
};

export type TInterventionsSignage = {
	id: number;
	county: string;
	road_name: string;
	ownership: number;
	road_system: number;
	speed_limit: number;
	road_seal: number;
	advisory_sign: string;
	advisory_sign_direction: string;
	advisory_sign_recommendation: string;
	advisory_sign_justification: string;
	advisory_plaque_value: number;
	advisory_plaque_recommendation: string;
	advisory_plaque_justification: string;
	accuracy_warning: string | undefined;
};

// --- Helpers used by attributesColumns ---

const round = (value: number, rounder: number) =>
	parseFloat((value / rounder).toFixed(0)) * rounder;

// TanStack-table cell — value converted to the client's unit system (header carries the label).
const unitCell = (fieldName: string, field: UnitField) => (info: { getValue: () => unknown }) =>
	formatUnitValue(
		fieldName,
		info.getValue() as number,
		field,
		unitSystemStore.systemFor(field.quantity)
	);

const unitHeader = (label: string, quantity: Quantity) => () =>
	`${label} (${getUnitLabel(quantity, unitSystemStore.systemFor(quantity))})`;

const unitValueCell = (fieldName: string, field: UnitField) => (value: CellContent) => {
	const system = unitSystemStore.systemFor(field.quantity);
	return `${formatUnitValue(fieldName, value as number, field, system)} ${getUnitLabel(field.quantity, system)}`;
};

// --- Attributes panel layout definitions ---
// Cell functions return string|number.

export const attributesIdentifyCurves: AttributesLayoutHelper = {
	title: { accessorKey: 'road_name' },
	subheaders: [
		[{ accessorKey: 'curve_context', cell: (value) => `CURVE, ${value}` }],
		[{ accessorKey: 'county' }]
	],
	body: [
		{
			accessorKey: 'curve_radius',
			header: () => 'Minimum radius',
			cell: unitValueCell('curve_radius', { quantity: 'length' })
		},
		{ accessorKey: 'curve_direction', header: () => 'Curve direction' }
	],
	table: [
		{
			accessorKey: 'road_seal',
			header: () => 'Surface',
			cell: lookupAttrCell(mapRoadSeal)
		},
		{
			accessorKey: 'ownership',
			header: () => 'Ownership',
			cell: lookupAttrCell(mapOwnership)
		},
		{
			accessorKey: 'road_system',
			header: () => 'Functional System',
			cell: lookupAttrCell(mapRoadSystem)
		},
		{
			accessorKey: 'speed_limit',
			header: () => 'Speed limit',
			cell: unitValueCell('speed_limit', { quantity: 'speed' })
		},
		{ accessorKey: 'aadt_band', header: () => 'AADT band' },
		{ accessorKey: 'start_position', header: () => 'Start position' },
		{ accessorKey: 'end_position', header: () => 'End position' }
	]
};

export const attributesPrioritizeCorridors: AttributesLayoutHelper = {
	title: {
		accessorKey: 'priority_rating',
		cell: (value) => priorityLabel(mapPriorityDescription, value)
	},
	subheaders: [
		[
			{
				accessorKey: 'road_name',
				show: (info) => info != null
			}
		],
		[{ accessorKey: 'county' }]
	],
	body: [
		{
			accessorKey: 'priority_rating_rank',
			header: () => 'Priority rating rank',
			show: (info) => info !== undefined
		},
		{
			accessorKey: 'no_priority_reason',
			header: () => 'Not prioritized reason',
			show: (info) => info !== undefined,
			cell: lookupAttrCell(mapNotPrioritizedReason)
		}
	],
	table: [
		{
			accessorKey: 'road_seal',
			header: () => 'Surface',
			cell: lookupAttrCell(mapRoadSeal)
		},
		{
			accessorKey: 'road_system',
			header: () => 'Functional System',
			cell: lookupAttrCell(mapRoadSystem)
		},
		{
			accessorKey: 'speed_limit',
			header: () => 'Speed limit',
			cell: unitValueCell('speed_limit', { quantity: 'speed' })
		},
		{
			accessorKey: 'alignment_category',
			header: () => 'Contains tortuous section?',
			cell: (info) => (info === 'Tortuous' ? 'Yes' : 'No')
		},
		{ accessorKey: 'aadt_band', header: () => 'AADT band' }
	]
};

export const attributesInterventionsSignage: AttributesLayoutHelper = {
	title: { accessorKey: 'road_name' },
	subheaders: [
		[{ accessorKey: 'advisory_sign', cell: (value) => (value as string).toLocaleUpperCase() }],
		[{ accessorKey: 'county' }]
	],
	body: [
		{ accessorKey: 'advisory_sign_recommendation', header: () => 'MUTCD Recommendation' },
		{ accessorKey: 'advisory_sign_direction', header: () => 'Sign direction' }
	],
	table: [
		{
			accessorKey: 'advisory_sign_justification',
			header: () => 'Justification (MUTCD 11th Edition, Table 2C-4)'
		},
		{ accessorKey: 'position', header: () => 'Position' }
	]
};

export const attributesInterventionsSpeeds: AttributesLayoutHelper = {
	title: { accessorKey: 'road_name' },
	subheaders: [
		[
			{
				accessorKey: 'advisory_sign',
				cell: () => 'Advisory speed plaque (W13-1P)'.toLocaleUpperCase()
			}
		],
		[{ accessorKey: 'county' }]
	],
	body: [
		{
			accessorKey: 'advisory_plaque_value',
			header: () => 'Advisory plaque value',
			cell: unitValueCell('advisory_plaque_value', { quantity: 'speed' })
		},
		{
			accessorKey: 'advisory_plaque_recommendation',
			header: () => 'MUTCD Recommendation',
			infoText: 'If horizontal alignment sign is installed'
		}
	],
	table: [
		{
			accessorKey: 'advisory_plaque_justification',
			header: () => 'Justification (MUTCD 11th Edition, Table 2C-6)'
		},
		{
			accessorKey: 'speed_limit',
			header: () => 'Speed limit',
			cell: unitValueCell('speed_limit', { quantity: 'speed' })
		},
		{ accessorKey: 'position', header: () => 'Position' }
	]
};

export const attributesPavementFrictionFocusAreas: AttributesLayoutHelper = {
	title: { cell: () => 'Pavement Friction Focus Area', accessorKey: 'id' },
	subheaders: [
		[
			{
				accessorKey: 'curve_class',
				cell: (info) => `Curve context: ${info} (max.)`
			}
		],
		[
			{
				accessorKey: 'deceleration_type',
				cell: (info) => pffaLabelFor(String(info))
			}
		]
	],
	body: [
		{
			accessorKey: 'treatment_length',
			header: () => 'Treatment area',
			cell: (info) => `${round((info as number) * 12, 500)} ft²` // FIXME: intlUnits
		}
	],
	table: [
		{
			accessorKey: 'deceleration_length',
			header: () => 'Deceleration length',
			cell: unitValueCell('deceleration_length', { quantity: 'length' })
		},
		{
			accessorKey: 'curve_length',
			header: () => 'Curve length',
			cell: unitValueCell('curve_length', { quantity: 'length' })
		},
		{ accessorKey: 'startpoint', header: () => 'Start position' },
		{ accessorKey: 'endpoint', header: () => 'End position' }
	]
};

export const attributesStoppingSightDistance: AttributesLayoutHelper = {
	title: { cell: () => 'Insufficient stopping sight distance', accessorKey: 'id' },
	subheaders: [[{ accessorKey: 'county' }]],
	table: [
		{ accessorKey: 'obstruction_type', header: () => 'Obstruction type' },
		{
			accessorKey: 'stopping_sight_distance',
			header: () => 'Stopping sight distance',
			show: (info) => info !== undefined,
			cell: unitValueCell('stopping_sight_distance', { quantity: 'length' })
		},
		{ accessorKey: 'startpoint', header: () => 'Start position' },
		{ accessorKey: 'endpoint', header: () => 'End position' }
	]
};

export const attributesSSDObstruction: AttributesLayoutHelper = {
	title: { cell: () => 'Stopping sight distance obstruction', accessorKey: 'id' },
	subheaders: [[{ accessorKey: 'county' }]],
	table: [{ accessorKey: 'obstruction_type', header: () => 'Obstruction type' }]
};

export const attributesSpeedLimitReview: AttributesLayoutHelper = {
	title: {
		accessorKey: 'slr_type',
		cell: (info) => (info === 'Enforce' ? 'Enforce Speed Limit' : 'Lower Speed Limit')
	},
	subheaders: [
		[
			{
				accessorKey: 'slr_type',
				cell: (info) =>
					info === 'Enforce'
						? 'Average corridor operating speed is more than 5mph above speed limit.'
						: 'Average corridor operating speed is more than 10mph below speed limit OR operating speed is less than speed limit for more than 10% of corridor length.'
			}
		]
	],
	table: [
		{ accessorKey: 'county', header: () => 'County' },
		{
			accessorKey: 'speed_limit',
			header: () => 'Current speed limit',
			cell: unitValueCell('speed_limit', { quantity: 'speed' })
		}
	]
};

export const attributesPassingSightDistance: AttributesLayoutHelper = {
	title: {
		accessorKey: 'road_seal',
		cell: (info) => (info === 1 ? 'No passing lines (visibility)' : 'No passing zone (visibility)')
	},
	subheaders: [[{ accessorKey: 'county' }]],
	table: [
		{
			accessorKey: 'road_seal',
			header: () => 'Surface',
			cell: lookupAttrCell(mapRoadSeal)
		},
		{ accessorKey: 'psd_justification', header: () => 'Justification' },
		{ accessorKey: 'obstruction_type', header: () => 'Obstruction type' },
		{ accessorKey: 'startpoint', header: () => 'Start position' },
		{ accessorKey: 'endpoint', header: () => 'End position' }
	]
};

export const attributesClearZone: AttributesLayoutHelper = {
	title: {
		accessorKey: 'clearzone_grade',
		cell: (info) => `Clear Zone grade: ${info}`
	},
	subheaders: [[{ accessorKey: 'road_name' }]],
	body: [{ accessorKey: 'clearzone_side' }, { accessorKey: 'segment_type' }],
	table: [
		{
			accessorKey: 'clearzone_grade_from',
			header: () => 'Grade from',
			cell: (e) => (e as string).charAt(0).toUpperCase() + (e as string).slice(1)
		},
		{
			accessorKey: 'clearzone_grade_justification',
			header: () => 'Grade justification'
		},
		{
			accessorKey: 'clearzone_start_offset_m',
			header: unitHeader('Start offset', 'length'),
			cell: unitValueCell('clearzone_start_offset_m', { quantity: 'length' })
		},
		{
			accessorKey: 'backslope_distance_m',
			header: unitHeader('AASHTO Backslope test distance', 'length'),
			cell: unitValueCell('backslope_distance_m', { quantity: 'length' })
		},
		{
			accessorKey: 'foreslope_distance_m',
			header: unitHeader('AASHTO Foreslope test distance', 'length'),
			cell: unitValueCell('foreslope_distance_m', { quantity: 'length' })
		},
		{
			accessorKey: 'clearzone_slope',
			header: () => 'Clear zone grade',
			cell: (info) => `${info}%`,
			show: (info) => info !== undefined
		},
		{
			accessorKey: 'drop_height_x',
			header: unitHeader('Drop', 'length'),
			cell: unitValueCell('drop_height_x', { quantity: 'length' }),
			show: (info) => info !== undefined
		},
		{
			accessorKey: 'rise_height_x',
			header: unitHeader('Rise', 'length'),
			cell: unitValueCell('rise_height_x', { quantity: 'length' }),
			show: (info) => info !== undefined
		},
		{
			accessorKey: 'speed_limit',
			header: () => 'Speed limit',
			cell: (info) => `${info} mph`
		},
		{
			accessorKey: 'start_position',
			header: () => 'Start position'
		},
		{
			accessorKey: 'end_position',
			header: () => 'End position'
		}
	]
};

// --- Table column definitions (per Table tab) ---

type TableTabId = 'Curves' | 'Corridors' | 'Signage';

export const tableColumnsByTab: Record<TableTabId, TableColumnDef<FeatureRow>[]> = {
	Curves: [
		{ accessorKey: 'road_name', header: 'Road name', meta: { bold: true } },
		{ accessorKey: 'county', header: 'Location' },
		{ accessorKey: 'curve_direction', header: 'Curve direction' },
		{
			accessorKey: 'speed_limit',
			header: unitHeader('Speed limit', 'speed'),
			cell: unitCell('speed_limit', { quantity: 'speed' })
		},
		{
			accessorKey: 'road_system',
			header: 'Functional System',
			cell: lookupCell(mapRoadSystem)
		},
		{
			accessorKey: 'road_seal',
			header: 'Surface',
			cell: lookupCell(mapRoadSeal)
		},
		{
			accessorKey: 'curve_radius',
			header: unitHeader('Minimum radius', 'length'),
			cell: unitCell('curve_radius', { quantity: 'length' })
		},
		{ accessorKey: 'curve_context', header: 'Curve context' },
		{ accessorKey: 'start_position', header: 'Start position', enableSorting: false },
		{ accessorKey: 'end_position', header: 'End position', enableSorting: false }
	],
	Corridors: [
		{
			accessorKey: 'road_name',
			header: 'Road name',
			meta: { bold: true, minVersion: PRIORITIZE_ROAD_NAME_MIN }
		},
		{ accessorKey: 'county', header: 'Location' },
		{
			accessorKey: 'speed_limit',
			header: unitHeader('Speed limit', 'speed'),
			cell: unitCell('speed_limit', { quantity: 'speed' })
		},
		{
			accessorKey: 'ownership',
			header: 'Ownership',
			cell: lookupCell(mapOwnership)
		},
		{
			accessorKey: 'road_system',
			header: 'Functional System',
			cell: lookupCell(mapRoadSystem)
		},
		{
			accessorKey: 'road_seal',
			header: 'Surface',
			cell: lookupCell(mapRoadSeal)
		},
		{
			accessorKey: 'priority_rating',
			header: 'Priority rating',
			cell: (info) => priorityLabel(mapPriorityDescription, info.getValue()),
			sortFn: sortByOrder(priorityDescriptionOrder)
		},
		{ accessorKey: 'priority_rating_rank', header: 'Priority Rating Rank' }
	],
	Signage: [
		{ accessorKey: 'road_name', header: 'Road name' },
		{ accessorKey: 'county', header: 'Location' },
		{
			accessorKey: 'speed_limit',
			header: unitHeader('Speed limit', 'speed'),
			cell: unitCell('speed_limit', { quantity: 'speed' })
		},
		{
			accessorKey: 'road_seal',
			header: 'Surface',
			cell: lookupCell(mapRoadSeal)
		},
		{ accessorKey: 'advisory_sign', header: 'Horizontal alignment sign' },
		{ accessorKey: 'advisory_sign_direction', header: 'Sign direction' },
		{ accessorKey: 'advisory_sign_recommendation', header: 'MUTCD recommendation' },
		{
			accessorKey: 'advisory_plaque_value',
			header: unitHeader('Advisory plaque value', 'speed'),
			cell: unitCell('advisory_plaque_value', { quantity: 'speed' })
		},
		{ accessorKey: 'advisory_plaque_recommendation', header: 'Advisory plaque recommendation' }
	]
};
